import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { TileEditMode } from "client/Features/Workspace/LeftSliders/EditTile/common";
import {
	AddTileTargetModel,
	DetailsTileModel,
	fetchTileModel
} from "client/Api/Workspace/workspace";
import {
	EditTile,
	EditTileHandle,
} from "../../Workspace/LeftSliders/EditTile/EditTile";
import { useAlerts } from "client/Hooks";
import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { appKindToNameMap } from "./AppCard";
import { AddTilesButton } from "./AddTiles/AddTilesButton/AddTilesButton";
import styles from "./CreateNewTileForm.module.less";
import { SpaceListItem } from "client/Api/Apps/AppStore/appStore";
import { navigateToWorkspace } from "client/Features/Workspace/routing";
import { setTrackingId } from "client/Misc/utils";

type CreateNewTileFormProps = {
	installedAppId: number;
	targetTileGroupId: number | null;
	targetSpace: SpaceListItem | null;
	activeSpaceId: number | null;
	onClose: () => void;
	onBack: () => void;
	onIsBusyChanged: (value: boolean) => void;
	onRefreshWorkspace: () => Promise<void>;
	onSwitchWorkspaceToEditMode: () => void;
};

export const CreateNewTileForm = (
	props: CreateNewTileFormProps
): ReactElement => {
	const {
		installedAppId,
		targetTileGroupId,
		targetSpace,
		activeSpaceId,
		onBack,
		onClose,
		onIsBusyChanged,
		onRefreshWorkspace,
		onSwitchWorkspaceToEditMode
	} = props;

	const [tileModel, setTileModel] = useState<DetailsTileModel | null>(null);
	const editTileRef: RefObject<EditTileHandle | null> = useRef<EditTileHandle>(null);
	const { showError } = useAlerts();

	const tileEditMode: TileEditMode = {
		kind: "create",
		installedAppId,
		targetTileGroupId,
	};

	const handleFormSubmit = useCallback(
		(target: AddTileTargetModel): void => {
			editTileRef.current?.createHandler?.(target);
		},
		[]
	);

	const handleSave = useCallback(
		async (): Promise<void> => {
			const shouldRedirect: boolean = targetSpace?.id !== activeSpaceId;

			onClose();

			await onRefreshWorkspace();

			if (shouldRedirect) {
				navigateToWorkspace(targetSpace?.id ?? undefined);

				return;
			}

			onSwitchWorkspaceToEditMode();
		},
		[
			activeSpaceId,
			onClose,
			onRefreshWorkspace,
			onSwitchWorkspaceToEditMode,
			targetSpace?.id
		]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();

			const getTileModel = async () => {
				try {
					const model: DetailsTileModel = await fetchTileModel(installedAppId, cancellation.signal);

					setTileModel(model);
				} catch (e) {
					showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
			};

			getTileModel();

			return () => {
				cancellation.abort();
			};
		},
		[installedAppId, showError]
	);

	const renderHeaderTitle = (): string | ReactElement => {
		if (tileModel) {
			const appName: string = appKindToNameMap[tileModel.appType];

			return (
				<FormattedText
					text={localize("_CreateNameTile")}
					thingsToReplace={[
						{ what: "{{Name}}", filler: appName }
					]}
				/>
			);
		}

		return localize("_CreateTile");
	};

	return (
		<>
			<Dialog.Header
				title={renderHeaderTitle()}
				onClose={onClose}
				onBack={onBack}
			/>
			<Dialog.Body goesEdgeToEdge>
				<div
					{...setTrackingId("create-new-tile-form-content")}
					className={styles.formContent}>
					{tileModel && (
						<EditTile
							ref={editTileRef}
							model={tileModel}
							mode={tileEditMode}
							onAfterSave={handleSave}
							onIsBusyChanged={onIsBusyChanged}
						/>
					)}
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						onClick={onBack}
						text={localize("_Back")}
						responsiveBehavior="hide"
					/>
					<AddTilesButton
						targetSpace={targetSpace}
						targetTileGroupId={targetTileGroupId}
						onClick={handleFormSubmit}
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};

CreateNewTileForm.displayName = "CreateNewTileForm";
